import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, ArrowRight, TrendingUp, CalendarClock, Heart, PiggyBank, type LucideIcon } from 'lucide-react';

import MarkdownLite from '@/components/ui/MarkdownLite';
import { supabase } from '@/lib/supabase';

type ChatMsg = { role: 'user' | 'assistant'; text: string };

const SUGGESTIONS: { icon: LucideIcon; text: string }[] = [
    { icon: CalendarClock, text: 'Quali appuntamenti ho questa settimana?' },
    { icon: TrendingUp, text: 'Qual è il mio fatturato medio mensile?' },
    { icon: Heart, text: 'Quali sono i miei clienti più fedeli?' },
    { icon: PiggyBank, text: 'Posso permettermi un investimento di 5000€?' },
];

interface AssistantChatProps {
    /**
     * Deprecato: il contesto dati del salone è ora costruito lato server
     * dall'edge function salon-assistant (legge direttamente le tabelle del salone).
     * Mantenuto opzionale per retrocompatibilità dei chiamanti; non viene più inviato.
     */
    context?: any;
    /** Classe per l'area messaggi (default: riempie lo spazio disponibile). */
    listClassName?: string;
}

/**
 * Chat con l'assistente AI. Presentazionale e riusabile:
 * vive nella pagina /assistant (tab Assistente) e nel pannello flottante globale.
 */
const AssistantChat: React.FC<AssistantChatProps> = ({ listClassName = 'flex-1 min-h-0' }) => {
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);

    const sendMessage = async (textArg?: string) => {
        const q = (textArg ?? input).trim();
        if (!q || loading) return;
        // Storico dei messaggi PRECEDENTI (prima di aggiungere quello nuovo): serve all'AI
        // per interpretare i follow-up ("e chi ha fatto di più?" dopo aver parlato di sabato).
        const history = messages.slice(-10).map((m) => ({ role: m.role, text: m.text }));
        setMessages((prev) => [...prev, { role: 'user', text: q }]);
        setInput('');
        setLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke('salon-assistant', {
                body: { query: q, history },
            });
            if (error) throw error;
            const text = data?.text || data?.error || 'Nessuna risposta.';
            setMessages((prev) => [...prev, { role: 'assistant', text }]);
        } catch (_e) {
            setMessages((prev) => [...prev, { role: 'assistant', text: "Errore nel contattare l'assistente. Riprova tra poco." }]);
        } finally {
            setLoading(false);
        }
    };

    // Scrolla SOLO la lista dei messaggi (non la pagina): evita che l'header scatti via.
    useEffect(() => {
        const el = listRef.current;
        if (el && (messages.length > 0 || loading)) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
    }, [messages, loading]);

    const empty = messages.length === 0 && !loading;

    return (
        <div className="flex flex-col h-full min-h-0">
            <div ref={listRef} className={`rounded-2xl bg-gray-50/50 border border-gray-100 p-4 overflow-y-auto custom-scrollbar space-y-3.5 ${listClassName}`}>
                {empty && (
                    <div className="min-h-full flex flex-col items-center justify-center text-center px-1 py-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-clip-400 to-clip-600 text-white flex items-center justify-center shadow-lg shadow-clip-200/70 mb-4">
                            <Sparkles size={26} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Come posso aiutarti?</h3>
                        <p className="text-sm text-gray-400 mt-1 mb-5 max-w-xs">
                            Fammi una domanda sui dati del tuo salone, oppure parti da un suggerimento.
                        </p>
                        <div className="w-full grid grid-cols-1 gap-2">
                            {SUGGESTIONS.map((s) => (
                                <button
                                    key={s.text}
                                    type="button"
                                    onClick={() => sendMessage(s.text)}
                                    className="group flex items-center gap-3 w-full text-left p-3.5 rounded-2xl border border-gray-100 bg-white hover:border-clip-200 hover:bg-clip-50/40 shadow-sm hover:shadow transition-all"
                                >
                                    <span className="w-9 h-9 rounded-xl bg-clip-50 text-clip-500 flex items-center justify-center shrink-0 group-hover:bg-clip-100 transition-colors">
                                        <s.icon size={17} />
                                    </span>
                                    <span className="text-sm font-medium text-gray-700 flex-1">{s.text}</span>
                                    <ArrowRight size={15} className="text-gray-300 group-hover:text-clip-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m, i) => (
                    <div key={i} className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {m.role === 'assistant' && (
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-clip-400 to-clip-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                                <Sparkles size={14} />
                            </div>
                        )}
                        <div
                            className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${m.role === 'user'
                                ? 'bg-clip-500 text-white rounded-br-md whitespace-pre-wrap'
                                : 'bg-white border border-gray-100 text-gray-700 leading-relaxed rounded-bl-md'
                                }`}
                        >
                            {m.role === 'assistant' ? <MarkdownLite text={m.text} /> : m.text}
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="flex items-end gap-2 justify-start">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-clip-400 to-clip-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                            <Sparkles size={14} />
                        </div>
                        <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white border border-gray-100 shadow-sm flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-clip-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 bg-clip-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 bg-clip-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                )}
            </div>

            <form className="flex items-center gap-2 mt-3 shrink-0" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Scrivi una domanda…"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900"
                />
                <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    className="p-3 bg-clip-500 hover:bg-clip-600 text-white rounded-xl disabled:opacity-50 transition-colors shrink-0 flex items-center justify-center"
                >
                    <Send size={18} />
                </button>
            </form>
        </div>
    );
};

export default AssistantChat;
